"use client";

import { useState } from "react";
import { useRouter } from "next/navigation";
import { Popover } from "@astryxdesign/core/Popover";
import { Avatar } from "@astryxdesign/core/Avatar";
import { Button } from "@astryxdesign/core/Button";
import { VStack } from "@astryxdesign/core/VStack";
import { Divider } from "@astryxdesign/core/Divider";
import { Text } from "@astryxdesign/core/Text";
import { List, ListItem } from "@astryxdesign/core/List";
import { Icon } from "@astryxdesign/core/Icon";
import { researchIcons } from "@/lib/icons/research-icons";

export type LandingAccountUser = {
  name: string;
  email: string | null;
  image: string | null;
  isAdmin?: boolean;
};

type Props = {
  user: LandingAccountUser;
  placement?: "above" | "below";
  /** Только аватар в шапке, без имени. */
  compact?: boolean;
};

async function signOutFromLanding() {
  const csrfRes = await fetch("/api/auth/csrf", { cache: "no-store" });
  const csrf = (await csrfRes.json().catch(() => null)) as {
    csrfToken?: string;
  } | null;
  if (!csrf?.csrfToken) {
    window.location.assign("/");
    return;
  }
  await fetch("/api/auth/signout", {
    method: "POST",
    headers: { "Content-Type": "application/x-www-form-urlencoded" },
    body: new URLSearchParams({
      csrfToken: csrf.csrfToken,
      callbackUrl: "/",
    }),
  });
  window.location.assign("/");
}

export function LandingAccountMenu({
  user,
  placement = "below",
  compact = false,
}: Props) {
  const router = useRouter();
  const [open, setOpen] = useState(false);
  const [signingOut, setSigningOut] = useState(false);

  function closeAndNavigate(href: string) {
    setOpen(false);
    router.push(href);
  }

  async function handleSignOut() {
    setSigningOut(true);
    try {
      await signOutFromLanding();
    } finally {
      setSigningOut(false);
    }
  }

  return (
    <Popover
      label="Меню аккаунта"
      placement={placement}
      alignment="end"
      width={260}
      isOpen={open}
      onOpenChange={setOpen}
      content={
        <VStack gap={2} width="100%">
          <VStack gap={0} align="start" padding={1}>
            <Text weight="semibold">{user.name}</Text>
            <Text type="supporting" color="secondary" className="break-all">
              {user.email ?? "Email не указан"}
            </Text>
          </VStack>

          <Divider />

          <List density="compact">
            <ListItem
              label="Кабинет"
              startContent={
                <Icon icon={researchIcons.home} size="sm" color="secondary" />
              }
              onClick={() => closeAndNavigate("/dashboard")}
            />
            <ListItem
              label="Настройки профиля"
              startContent={
                <Icon icon={researchIcons.settings} size="sm" color="secondary" />
              }
              onClick={() => closeAndNavigate("/settings")}
            />
            {user.isAdmin ? (
              <ListItem
                label="Админка"
                startContent={
                  <Icon
                    icon={researchIcons.layoutDashboard}
                    size="sm"
                    color="secondary"
                  />
                }
                onClick={() => closeAndNavigate("/admin")}
              />
            ) : null}
          </List>

          <VStack width="100%" align="stretch">
            <Button
              label="Выйти"
              variant="ghost"
              size="sm"
              type="button"
              isDisabled={signingOut}
              onClick={() => void handleSignOut()}
              icon={
                <Icon icon={researchIcons.logOut} size="sm" color="secondary" />
              }
            />
          </VStack>
        </VStack>
      }
    >
      {(triggerProps) => (
        <button
          {...triggerProps}
          type="button"
          title={user.name}
          aria-label={`Аккаунт: ${user.name}`}
          className={
            compact
              ? "inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-full transition-colors hover:bg-muted focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2"
              : "inline-flex max-w-[11rem] items-center gap-2 rounded-md px-2 py-1.5 text-sm font-medium text-primary transition-colors hover:bg-muted focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2"
          }
        >
          <Avatar
            name={user.name}
            src={user.image ?? undefined}
            size="xsmall"
            alt={user.name}
          />
          {!compact ? (
            <>
              <span className="hidden truncate sm:inline">{user.name}</span>
              <Icon
                icon={researchIcons.chevronRight}
                size="sm"
                color="secondary"
                className="hidden sm:inline"
              />
            </>
          ) : null}
        </button>
      )}
    </Popover>
  );
}
